import jsonLogic from "json-logic-js"
import React, {createContext, useContext, useEffect, useRef, useState} from "react"
import {useLocation, useNavigate} from "react-router-dom"
import {DataContext} from "./DataContextProvider"
import {ActionContext} from "./ActionContextProvider"
import TomClient from "../../anwendungskonfigurator/services/tomClient"

export const WORKFLOW_ID_POINTER = "/workflow/activeWorkflowId"
export const WORKFLOW_STEP_POINTER = "/workflow/currentStepId"

export const WORKFLOW_EXIT_PATH = "/"

const FINISH = "FINISH"

const basePathOf = (pathname) => pathname.replace(/\/[^/]*$/, "")

export const WorkflowContext = createContext({
    advance: () => {
    },
    cancelWorkflow: () => {
    },
    getButtonConfig: () => ({}),
    hasTransition: () => false,
    ensureWorkflow: () => {},
    getWorkflowById: () => null,
    isWorkflowLoaded: () => false,
    orderedSteps: [],
})

const conditionPasses = (condition, data) => {
    if (condition == null) return true
    if (typeof condition === "object" && Object.keys(condition).length === 0) {
        return true
    }
    try {
        return Boolean(jsonLogic.apply(condition, data))
    } catch (error) {
        console.error("[Workflow] Fehler beim Auswerten einer Condition", condition, error)
        return false
    }
}

/**
 * Sortiert die Workflow-Schritte basierend auf den Transitionen in die richtige chronologische Reihenfolge.
 * @param {Object} workflow - Das gesamte Workflow-JSON-Objekt.
 * @param {string} currentWorkflowStepId - Die aktive ID im Workflow.
 * @returns {Array} Ein sortiertes Array mit den Schritt-Objekten.
 */
function getOrderedSteps(workflow, currentWorkflowStepId) {
    if (!workflow || !workflow.steps || !workflow.transitions) {
        return []
    }

    const stepMap = new Map(workflow.steps.map(step => [step.id, step]))

    const forwardTransitions = new Map()

    workflow.transitions.forEach(transition => {
        const sourceStep = stepMap.get(transition.sourceStepId)
        if (!sourceStep) return

        const buttonConfig = sourceStep.buttonConfig?.[transition.sourceButtonId]

        if ((buttonConfig && buttonConfig.text === "weiter") || transition.sourceStepId === workflow.startStepId) {
            forwardTransitions.set(transition.sourceStepId, transition.targetStepId)
        }
    })

    const orderedSteps = []
    let currentStepId = workflow.startStepId

    while (currentStepId && orderedSteps.length < workflow.steps.length) {
        const currentStep = stepMap.get(currentStepId)
        if (!currentStep) break

        orderedSteps.push(currentStep)

        currentStepId = forwardTransitions.get(currentStepId)
    }

    return orderedSteps.map((el) => ({
        title: el.title,
        isActive: currentWorkflowStepId === el.id,
    }))
}

const WorkflowContextProvider = ({children}) => {
    const {data, getDataFromPointer} = useContext(DataContext)
    const {triggerAction} = useContext(ActionContext)

    const navigate = useNavigate()
    const location = useLocation()

    const [workflowsById, setWorkflowsById] = useState({})
    const pendingLoads = useRef(new Set())
    const activeWorkflowId = getDataFromPointer(WORKFLOW_ID_POINTER)

    const ensureWorkflow = (workflowId) => {
        if (!workflowId) return
        if (workflowId in workflowsById || pendingLoads.current.has(workflowId)) return
        pendingLoads.current.add(workflowId)
        TomClient.getEnrich(`/workflow/${workflowId}`)
            .then((response) =>
                setWorkflowsById((previous) => ({
                    ...previous,
                    [workflowId]: response?.workflow ?? null,
                })),
            )
            .catch((error) => {
                console.error(`[Workflow] "${workflowId}" nicht ladbar`, error)
                setWorkflowsById((previous) => ({...previous, [workflowId]: null}))
            })
            .finally(() => pendingLoads.current.delete(workflowId))
    }

    useEffect(() => ensureWorkflow(activeWorkflowId), [activeWorkflowId])

    const getWorkflowById = (workflowId) => workflowsById[workflowId] ?? null
    const isWorkflowLoaded = (workflowId) => workflowId in workflowsById
    const getStepById = (workflow, stepId) =>
        workflow?.steps?.find((step) => step.id === stepId) ?? null

    const finishWorkflow = (workflow) => {
        if (workflow?.onFinish) {
            triggerAction(workflow.onFinish)
        }
        navigate(WORKFLOW_EXIT_PATH)
    }

    const advance = (buttonId) => {
        const activeWorkflowId = getDataFromPointer(WORKFLOW_ID_POINTER)
        const workflow = getWorkflowById(activeWorkflowId)
        if (!workflow) {
            console.warn("[Workflow] advance ohne aktiven Workflow aufgerufen.")
            return
        }
        const currentStepId = getDataFromPointer(WORKFLOW_STEP_POINTER)
        const candidates = (workflow.transitions ?? []).filter(
            (transition) =>
                transition.sourceStepId === currentStepId &&
                transition.sourceButtonId === buttonId,
        )
        if (!candidates.length) {
            console.warn(
                `[Workflow] Keine Transition für Step "${currentStepId}" und Button "${buttonId}".`,
            )
            return
        }
        const match = candidates.find((transition) =>
            conditionPasses(transition.condition, data),
        )
        if (!match) {
            console.warn(
                `[Workflow] Keine Transition mit erfüllter Condition für Button "${buttonId}".`,
            )
            return
        }
        const targetStep = getStepById(workflow, match.targetStepId)
        if (!targetStep) {
            console.error(
                `[Workflow] targetStepId "${match.targetStepId}" verweist auf keinen Step.`,
            )
            return
        }
        if (targetStep.rootType === FINISH) {
            finishWorkflow(workflow)
            return
        }
        navigate(`${basePathOf(location.pathname)}/${targetStep.id}`)
    }

    const cancelWorkflow = () => navigate(WORKFLOW_EXIT_PATH)

    const activeWorkflow = activeWorkflowId ? getWorkflowById(activeWorkflowId) : null
    const currentStepId = getDataFromPointer(WORKFLOW_STEP_POINTER)
    const currentStep = activeWorkflow
        ? getStepById(activeWorkflow, currentStepId)
        : null

    const getButtonConfig = (buttonId) =>
        currentStep?.buttonConfig?.[buttonId] ?? {}


    const hasTransition = (buttonId) =>
        !!activeWorkflow &&
        !!currentStep &&
        (activeWorkflow.transitions ?? []).some(
            (transition) =>
                transition.sourceStepId === currentStep.id &&
                transition.sourceButtonId === buttonId,
        )

    const value = {
        advance,
        cancelWorkflow,
        getButtonConfig,
        hasTransition,
        ensureWorkflow,
        getWorkflowById,
        isWorkflowLoaded,
        orderedSteps: getOrderedSteps(activeWorkflow, currentStep?.id),
    }

    return (
        <WorkflowContext.Provider value={value}>
            {children}
        </WorkflowContext.Provider>
    )
}

export default WorkflowContextProvider
